import { Badge } from "@mantine/core";
import { IconTruck } from "@tabler/icons-react";
import { useClient } from "#/hook/useClient";
import { usePrvTranslation } from "#/pages/ClientDetail/components/prv/usePrvTranslation";
import type { PredictionBoard, ShipmentCard } from "#/types/prediction";
import { hexToRgba } from "#/utils/ColorUtils";
import { formatDate, isSameLocalDay } from "#/utils/dateTimeUtils";
import {
  anchorOf,
  categoryColor,
  categoryName,
  isPastShipment,
  lateDays,
  outcomeOf,
} from "../predictionUtils";

interface Props {
  card: ShipmentCard;
  board: Pick<PredictionBoard, "categories">;
  now: Date;
  onOpen: (shipmentId: number) => void;
}

const OUTCOME_COLOR = {
  delivered: "green",
  early: "teal",
  late: "orange",
  not_delivered: "red",
  pending: "gray",
} as const;

/**
 * One shipment as a card in a day column of the week calendar. One stop:
 * the sub-client, its address, the end of its due window when it spans more
 * than the day, and the load per category with advice chips. Several stops:
 * a "N deliveries" line, then each stop with its own load. Trucks are left
 * to the detail page. A shipment whose day has passed is muted and shows,
 * per stop, what happened instead of the load. Click opens the shipment.
 */
const ShipmentBlock = ({ card, board, now, onOpen }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();
  const past = isPastShipment(card, now);
  const anchor = anchorOf(card);
  const stops = [...card.predictions].sort((a, b) =>
    a.id === anchor?.id
      ? -1
      : b.id === anchor?.id
        ? 1
        : a.subClient.name.localeCompare(b.subClient.name)
  );
  const grouped = stops.length > 1;

  // With a live plan the stops are shown per trip, in driving order, on a
  // small timeline; a TRIP label separates the trips
  // when there are several. Stops no trip carries come last, unlabelled. A
  // past shipment has no live plan, so it shows the plain list.
  const trips = !past && card.plan ? card.plan.trips : [];
  const byPrediction = new Map(stops.map((prediction) => [prediction.id, prediction]));
  type StopRow = { prediction: (typeof stops)[number] };
  const tripGroups = trips.map((trip) => ({
    index: trip.index,
    stops: trip.stopOrders.flatMap((order): StopRow[] => {
      const stop = card.plan!.stops.find((candidate) => candidate.order === order);
      const prediction = stop ? byPrediction.get(stop.predictionId) : undefined;
      return prediction ? [{ prediction }] : [];
    }),
  }));
  const onTrip = new Set(
    tripGroups.flatMap((group) => group.stops.map((stop) => stop.prediction.id))
  );
  const leftOver: StopRow[] = stops
    .filter((stop) => trips.length === 0 || !onTrip.has(stop.id))
    .map((prediction) => ({ prediction }));
  const groups: { index: number | null; stops: StopRow[] }[] = [
    ...tripGroups.filter((group) => group.stops.length > 0),
    ...(leftOver.length > 0 ? [{ index: null, stops: leftOver }] : []),
  ];
  /** The rail (circles + dashed line) only earns its place when a trip has two or more stops. */
  const railFor = (group: { index: number | null; stops: StopRow[] }) =>
    trips.length > 0 && group.index !== null && group.stops.length > 1;

  return (
    <button
      type="button"
      onClick={() => onOpen(card.shipment.id)}
      className={`flex w-full flex-col gap-1.5 overflow-hidden rounded-md border px-2 py-1.5 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:ring-2 focus-visible:ring-[var(--client-color,#3f81ef)] focus-visible:ring-offset-1 focus-visible:outline-none ${
        past
          ? "border-gray-200 bg-gray-50 opacity-70 hover:opacity-100"
          : "border-gray-200 bg-white"
      }`}
    >
      {/* Past cards have no live plan, so no trip count: the strip goes grey and says deliveries only. */}
      <span
        className={`-mx-2 -mt-1.5 mb-0.5 flex flex-col text-[11px] font-semibold tracking-wider uppercase ${
          past ? "text-gray-600" : "text-indigo-700"
        }`}
      >
        {/* The starting Site first, once, on its own darker band: it is the same for every trip. */}
        {!past && card.plan && (
          <span
            className={`flex items-center bg-indigo-100 px-2 py-1.5 ${card.plan.origin ? "" : "text-red-700"}`}
          >
            {card.plan.origin ? card.plan.origin.name : t("predictedDeliveries.block.noSite")}
          </span>
        )}
        <span
          className={`flex items-center gap-1 px-2 py-1.5 ${past ? "bg-gray-100" : "bg-indigo-50"}`}
        >
          <IconTruck size={12} />
          {t("predictedDeliveries.block.deliveries", { count: stops.length })}
          {trips.length > 0 && (
            <>
              {" · "}
              {t("predictedDeliveries.block.trips", { count: trips.length })}
            </>
          )}
        </span>
      </span>
      {groups.map((group, groupIndex) => (
        <span key={group.index ?? "rest"} className="flex flex-col gap-1.5">
          {/* Between trips: a plain light rule. */}
          {groupIndex > 0 && <span className="my-1 h-px w-full bg-gray-200" />}
          {group.index !== null && trips.length > 1 && (
            <span className="text-[11px] font-black tracking-wider text-indigo-700 uppercase">
              {t("predictedDeliveries.shipment.trips.trip", { index: group.index })}
            </span>
          )}
          <span className="flex flex-col gap-4">
            {group.stops.map(({ prediction }, index) => {
              const windowEnd =
                prediction.dueTo && !isSameLocalDay(prediction.dueFrom, prediction.dueTo)
                  ? formatDate(prediction.dueTo, client)
                  : null;
              const outcome = outcomeOf(prediction, now);
              return (
                <span
                  key={prediction.id}
                  className={`flex gap-2 ${
                    !railFor(group) && index > 0 ? "border-t border-gray-100 pt-1.5" : ""
                  }`}
                >
                  {/* Fixed-width rail: the dot and the line to the next dot never move with the text. */}
                  {railFor(group) && (
                    <span className="flex w-3 shrink-0 flex-col items-center">
                      <svg
                        width={10}
                        height={10}
                        viewBox="0 0 10 10"
                        aria-hidden="true"
                        className="mt-1.5 shrink-0 text-black/90"
                      >
                        <circle
                          cx={5}
                          cy={5}
                          r={4}
                          fill="white"
                          stroke="currentColor"
                          strokeWidth={1.25}
                        />
                      </svg>
                      {index < group.stops.length - 1 && (
                        <span className="mt-1 -mb-4 w-0 flex-1 border-l border-dashed border-black/90" />
                      )}
                    </span>
                  )}
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    {/* Name and address as one tight block. */}
                    <span className="flex flex-col">
                      <span className="text-sm leading-tight font-semibold break-words text-gray-900">
                        {prediction.subClient.name}
                      </span>
                      {prediction.subClient.address && (
                        <span className="text-xs leading-tight break-words text-gray-500">
                          {prediction.subClient.address}
                        </span>
                      )}
                      {windowEnd && !grouped && !past && (
                        <span className="text-xs text-gray-600 tabular-nums">
                          {t("predictedDeliveries.card.until", { date: windowEnd })}
                        </span>
                      )}
                    </span>
                    {past ? (
                      <Badge
                        size="xs"
                        variant="light"
                        color={OUTCOME_COLOR[outcome]}
                        className="w-fit normal-case"
                      >
                        {t(`predictedDeliveries.outcome.${outcome}`, {
                          count: lateDays(prediction, now),
                        })}
                      </Badge>
                    ) : prediction.contentUnknown ? (
                      <span className="text-xs leading-snug text-gray-600 italic">
                        {t("predictedDeliveries.card.contentUnknown")}
                      </span>
                    ) : (
                      <span className="mt-1 flex flex-wrap gap-1">
                        {prediction.lines.map((line) => {
                          const color = categoryColor(board, line.productId) ?? "#94a3b8";
                          return (
                            <span
                              key={line.productId}
                              className="inline-flex h-6 items-center gap-1 rounded-full border px-2 text-xs leading-none font-medium whitespace-nowrap"
                              style={{
                                backgroundColor: hexToRgba(color, 0.1),
                                borderColor: color,
                                color,
                              }}
                            >
                              <span className="font-bold">{line.quantity}</span>
                              {categoryName(board, line.productId)}
                            </span>
                          );
                        })}
                      </span>
                    )}
                  </span>
                </span>
              );
            })}
          </span>
        </span>
      ))}
    </button>
  );
};

export default ShipmentBlock;
